import { DBMetadataDB } from '@/db/repositories/DBMetadataRepo';
import { TourBagService, TourBagStaticService, OrderStaticService } from '.';
import { getUser } from '@/library/UserSessionHelper';
import { getOrderNotificationTypeIds } from '@/library/OrderSettingsHelper';

/**
 * Runs all TourBagStaticService sync functions that have stale TTL.
 * Each sync function internally checks staleness via genericSync/isDBEntryStale,
 * so we simply call them all and let the TTL system skip fresh entries.
 */
export const runAllSyncs = async (options?: { forceUpdate?: boolean }) => {
  const forceUpdate = options?.forceUpdate ?? false;

  // Fetch all DB metadata in one go to avoid 60+ individual queries
  const [metadataDict, user] = await Promise.all([DBMetadataDB.getAll(), getUser()]);
  const syncOptions = { forceUpdate, metadataDict };

  await Promise.all([
    // Sync All Player TourBag
    TourBagService.syncAllTourBagData(),
    // Sync Dropdown Data
    TourBagStaticService.syncHeadTypeLofts(syncOptions),
    TourBagStaticService.syncHeadTypeFamilies(syncOptions),
    TourBagStaticService.syncFamilies(syncOptions),
    TourBagStaticService.syncHeadModelHandsForDropdown(syncOptions),
    TourBagStaticService.syncHeadModelByManufacturerAndTypeForDropdownDto(syncOptions),
    TourBagStaticService.syncHeadModelByManufacturerAndTypeForDropdownDtoFairways(syncOptions),
    TourBagStaticService.syncHeadModelByManufacturerAndTypeForDropdownDtoHybrids(syncOptions),
    TourBagStaticService.syncHeadModelByManufacturerAndTypeForDropdownDtoWedges(syncOptions),
    TourBagStaticService.syncHandLookups(syncOptions),
    TourBagStaticService.syncCtLocations(syncOptions),
    TourBagStaticService.syncSleeves(syncOptions),
    TourBagStaticService.syncSwingWtPrefixes(syncOptions),
    TourBagStaticService.syncSwingWtSuffixes(syncOptions),
    TourBagStaticService.syncGripSizes(syncOptions),
    TourBagStaticService.syncFractions(syncOptions),
    TourBagStaticService.syncHeadWeightLocationLookups(syncOptions),
    TourBagStaticService.syncHeadWeightLocations(syncOptions),
    TourBagStaticService.syncHeadWeightNumberLookups(syncOptions),
    TourBagStaticService.syncHeadWeightNumbers(syncOptions),
    TourBagStaticService.syncHeadWeightLocationAft(syncOptions),
    TourBagStaticService.syncHeadWeightLocationBack(syncOptions),
    TourBagStaticService.syncHeadWeightNumberAft(syncOptions),
    TourBagStaticService.syncHeadWeightNumberBack(syncOptions),
    TourBagStaticService.syncSettings(syncOptions),
    TourBagStaticService.syncShaftByShaftNameForDropdowns(syncOptions),
    TourBagStaticService.syncIronShaftByShaftNameForDropdowns(syncOptions),
    TourBagStaticService.syncWedgeShaftByShaftNameForDropdowns(syncOptions),
    TourBagStaticService.syncHybridShaftByShaftNameForDropdowns(syncOptions),
    TourBagStaticService.syncShaftLengthDigits(syncOptions),
    TourBagStaticService.syncShaftLengthFractions(syncOptions),
    TourBagStaticService.syncShaftTippingFractions(syncOptions),
    TourBagStaticService.syncGripTypes(syncOptions),
    TourBagStaticService.syncDigits(syncOptions),
    TourBagStaticService.syncLieDigits(syncOptions),
    TourBagStaticService.syncLieFractions(syncOptions),
    TourBagStaticService.syncLofts(syncOptions),
    TourBagStaticService.syncLoftDigits(syncOptions),
    TourBagStaticService.syncLoftFractions(syncOptions),
    TourBagStaticService.syncPutterFace(syncOptions),
    TourBagStaticService.syncPutterGrip(syncOptions),
    TourBagStaticService.syncPutterHeadFinish(syncOptions),
    TourBagStaticService.syncPutterHeadModel(syncOptions),
    TourBagStaticService.syncPutterLevel(syncOptions),
    TourBagStaticService.syncPutterNeck(syncOptions),
    TourBagStaticService.syncPutterShaft(syncOptions),
    TourBagStaticService.syncPutterSightline(syncOptions),
    TourBagStaticService.syncPutterType(syncOptions),
    TourBagStaticService.syncPutterWeight(syncOptions),
    TourBagStaticService.syncHeadFinishWedge(syncOptions),
    TourBagStaticService.syncIronHeadModelByManufacturerAndTypeForDropdownDto(syncOptions),
    TourBagStaticService.syncIronClub(syncOptions),
    TourBagStaticService.syncBsgBall(syncOptions),
    TourBagStaticService.syncBsgGlove(syncOptions),
    TourBagStaticService.syncBsgGloveSize(syncOptions),
    TourBagStaticService.syncBsgHeadwear(syncOptions),
    TourBagStaticService.syncBsgShoe(syncOptions),
    TourBagStaticService.syncBsgShoeSize(syncOptions),
    TourBagStaticService.syncBsgShoeWidth(syncOptions),
    TourBagStaticService.syncGolfBag(syncOptions),
    TourBagStaticService.syncTours(syncOptions),
    TourBagStaticService.syncLoftsAndHandAndHeadModel(syncOptions),
    TourBagStaticService.syncPlayerRegionLookups(syncOptions),
    TourBagStaticService.syncPlayerStatusLookups(syncOptions),
    TourBagStaticService.syncPlayerInternalStatuses(syncOptions),
    TourBagStaticService.syncInternalStatusLookups(syncOptions),
    TourBagStaticService.syncNationalities(syncOptions),
    TourBagStaticService.syncPlayerCollegeLookups(syncOptions),
    TourBagStaticService.syncPlayerYearLookups(syncOptions),
    TourBagStaticService.syncPlayerSupportLookups(syncOptions),
    OrderStaticService.syncTournaments(syncOptions),
    OrderStaticService.syncOrderNotificationEvents(getOrderNotificationTypeIds(user?.orderSettings), syncOptions),
    OrderStaticService.syncOrderStatusList(syncOptions),
    OrderStaticService.syncBuildLocationsForDropdown(syncOptions),
    OrderStaticService.syncAppUsersForDropdown(syncOptions),
    OrderStaticService.syncOrderAccountsForDropdown(syncOptions),
  ]);
};
